import { Box, Group, Loader, Paper, Stack } from '@mantine/core';
import { UserGetResponse } from 'api/actions/user-get/user-get-response';
import { useApi } from 'api/api-context';
import { AxiosError } from 'axios';
import H2Medium from 'components/typography/H2Medium';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { USERS_PAGE_PATH, WELCOME_PAGE_PATH } from 'routes/paths';
import { NO_ENTITY_EXISTS_ERROR_CODE } from 'utils/constants';
import Button from 'components/Button';

/**
 * Page used to trigger admin impersonation from a secret path.
 */
export default function ImpersonateUserPage() {
  const navigate = useNavigate();
  const { userId: contextUserId, roleId, getAction, setImpersonate } = useApi();
  const { userId: userIdRaw } = useParams();
  const [targetUser, setTargetUser] = useState<UserGetResponse | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [userExists, setUserExists] = useState(true);
  const [loading, setLoading] = useState(false);

  const userId = useMemo(() => {
    const parsed = Number(userIdRaw);

    if (!Number.isInteger(parsed) || parsed <= 0) {
      return undefined;
    }

    return parsed;
  }, [userIdRaw]);

  const isAdmin = roleId === ROLE_ADMIN_ID;
  const isInvalidUserId = userId === undefined;
  const isSelfImpersonation = userId === contextUserId;

  useEffect(() => {
    if (!userId) {
      setIsUserLoading(false);
      return;
    }

    const userGetAction = getAction('UserGet');

    setIsUserLoading(true);
    setUserExists(true);

    userGetAction({ parameters: { userId: String(userId) } })
      .then((response) => {
        setTargetUser(response);
      })
      .catch((error) => {
        if (
          (error instanceof AxiosError && error.response?.status === 404) ||
          error?.response?.data?.error?.code === NO_ENTITY_EXISTS_ERROR_CODE
        ) {
          setUserExists(false);
          setTargetUser(null);
          return;
        }

        panic(error);
      })
      .finally(() => setIsUserLoading(false));
  }, [userId, getAction]);

  /**
   * Confirms and executes user impersonation.
   */
  const onConfirm = useCallback(() => {
    if (!userId || loading) {
      return;
    }

    const userGetMeAction = getAction('AuthLoggedUserInfo');

    setLoading(true);

    userGetMeAction({ headers: { 'X-Impersonate': userId } })
      .then((user) => {
        setImpersonate(user);
        navigate(WELCOME_PAGE_PATH.original);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, [userId, loading, getAction, navigate, setImpersonate]);

  if (!isAdmin || isInvalidUserId || isSelfImpersonation) {
    return <Navigate to={USERS_PAGE_PATH.original} replace />;
  }

  return (
    <DashboardLayout
      title="Vystupovať ako používateľ"
      breadcrumbs={[{ title: 'Používatelia', link: USERS_PAGE_PATH.original }, { title: 'Vystupovať ako používateľ' }]}
      groupPosition="left"
    >
      <Paper maw={640} p={24} radius="lg" withBorder>
        <Stack spacing={12}>
          <H2Medium color="gray.8">Potvrdenie vystupovania ako používateľ</H2Medium>
          {isUserLoading ? (
            <Group spacing={12}>
              <Loader size="sm" />
              <P2Regular color="gray.7">Načítavam informácie o používateľovi.</P2Regular>
            </Group>
          ) : userExists && targetUser ? (
            <>
              <P2Regular color="gray.7">Používateľ bol nájdený. Pred pokračovaním skontrolujte jeho údaje.</P2Regular>
              <Box>
                <P1Medium color="gray.8">{targetUser.fullName}</P1Medium>
                <P2Regular color="gray.7">ID: {targetUser.userId}</P2Regular>
                <P2Regular color="gray.7">E-mail: {targetUser.email}</P2Regular>
                <P2Regular color="gray.7">Organizácia: {targetUser.organizationName}</P2Regular>
              </Box>
            </>
          ) : (
            <P2Regular color="red.8">Používateľ s ID {userId} neexistuje. Vystupovanie nie je možné spustiť.</P2Regular>
          )}
          <Group mt={8}>
            <Button size="md" variant="secondary" onClick={() => navigate(USERS_PAGE_PATH.original)}>
              Zrušiť
            </Button>
            <Button
              size="md"
              onClick={onConfirm}
              loading={loading}
              disabled={isUserLoading || !userExists || !targetUser}
            >
              Potvrdiť a pokračovať
            </Button>
          </Group>
        </Stack>
      </Paper>
    </DashboardLayout>
  );
}
